import React, { useState, useEffect } from 'react';
import './CustomerSupport.css';

const CustomerSupport = () => {
  const [customers, setCustomers] = useState([
    {
      id: 1,
      name: 'John Doe',
      email: 'john@example.com',
      avatar: 'JD',
      lastMessage: 'What insurance options do you offer?',
      timestamp: '2024-01-15 10:30',
      messages: [
        { id: 1, sender: 'customer', text: 'Hi, I have a question about your rental services.', timestamp: '2024-01-15 10:15' },
        { id: 2, sender: 'customer', text: 'What insurance options do you offer?', timestamp: '2024-01-15 10:30' }
      ]
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'jane@example.com',
      avatar: 'JS',
      lastMessage: 'Great, thank you for the information!',
      timestamp: '2024-01-14 15:45',
      messages: [
        { id: 1, sender: 'customer', text: 'Do you have luxury vehicles available?', timestamp: '2024-01-14 15:20' },
        { id: 2, sender: 'admin', text: 'Yes, we have a range of luxury cars available. Would you like to see our premium fleet?', timestamp: '2024-01-14 15:35' },
        { id: 3, sender: 'customer', text: 'Great, thank you for the information!', timestamp: '2024-01-14 15:45' }
      ]
    }
  ]);

  const [selectedCustomerId, setSelectedCustomerId] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState(customers);

  // Load customer questions from localStorage
  useEffect(() => {
    const storedQuestions = localStorage.getItem('contactFormSubmissions');
    if (storedQuestions) {
      try {
        const parsed = JSON.parse(storedQuestions);
        const newCustomers = parsed.map((q, index) => ({
          id: 100 + index,
          name: q.firstName + ' ' + q.lastName,
          email: q.email,
          avatar: (q.firstName[0] + q.lastName[0]).toUpperCase(),
          lastMessage: q.message.substring(0, 50) + (q.message.length > 50 ? '...' : ''),
          timestamp: new Date(q.timestamp).toLocaleString(),
          messages: [
            {
              id: 1,
              sender: 'customer',
              text: q.message,
              timestamp: new Date(q.timestamp).toLocaleString()
            }
          ]
        }));
        setCustomers(prev => [...prev, ...newCustomers]);
        setFilteredCustomers(prev => [...prev, ...newCustomers]);
        localStorage.removeItem('contactFormSubmissions');
      } catch (error) {
        console.error('Error loading contact submissions:', error);
      }
    }
  }, []);

  // Handle search
  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = customers.filter(customer =>
      customer.name.toLowerCase().includes(query) ||
      customer.email.toLowerCase().includes(query) ||
      customer.lastMessage.toLowerCase().includes(query)
    );
    setFilteredCustomers(filtered);
  }, [searchQuery, customers]);

  // Handle reply submission
  const handleSendReply = () => {
    if (!replyText.trim()) return;

    const updatedCustomers = customers.map(customer => {
      if (customer.id === selectedCustomerId) {
        const updatedMessages = [
          ...customer.messages,
          {
            id: customer.messages.length + 1,
            sender: 'admin',
            text: replyText,
            timestamp: new Date().toLocaleString()
          }
        ];
        return {
          ...customer,
          messages: updatedMessages,
          lastMessage: replyText.substring(0, 50) + (replyText.length > 50 ? '...' : ''),
          timestamp: new Date().toLocaleString()
        };
      }
      return customer;
    });

    setCustomers(updatedCustomers);
    setReplyText('');
    
    // Update search results
    const query = searchQuery.toLowerCase();
    const filtered = updatedCustomers.filter(customer =>
      customer.name.toLowerCase().includes(query) ||
      customer.email.toLowerCase().includes(query) ||
      customer.lastMessage.toLowerCase().includes(query)
    );
    setFilteredCustomers(filtered);
  };

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  return (
    <div className="customer-support-page">
      {/* Customers List */}
      <div className="customers-panel">
        <div className="panel-header">
          <h2>Messages</h2>
        </div>
        
        <div className="search-container">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="customers-list">
          {filteredCustomers.length === 0 ? (
            <div className="empty-state">No customers found</div>
          ) : (
            filteredCustomers.map(customer => (
              <div
                key={customer.id}
                className={`customer-item ${selectedCustomerId === customer.id ? 'active' : ''}`}
                onClick={() => setSelectedCustomerId(customer.id)}
              >
                <div className="customer-avatar">{customer.avatar}</div>
                <div className="customer-info">
                  <div className="customer-name">{customer.name}</div>
                  <div className="customer-preview">{customer.lastMessage}</div>
                </div>
                <div className="customer-timestamp">{customer.timestamp.split(' ')[1]}</div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Conversation Panel */}
      <div className="conversation-panel">
        {selectedCustomer ? (
          <>
            {/* Conversation Header */}
            <div className="conversation-header">
              <div className="header-info">
                <div className="header-avatar">{selectedCustomer.avatar}</div>
                <div className="header-details">
                  <div className="header-name">{selectedCustomer.name}</div>
                  <div className="header-email">{selectedCustomer.email}</div>
                </div>
              </div>
            </div>

            {/* Messages Container */}
            <div className="messages-container">
              {selectedCustomer.messages.map(message => (
                <div key={message.id} className={`message ${message.sender}`}>
                  <div className="message-content">
                    <div className="message-text">{message.text}</div>
                    <div className="message-time">{message.timestamp}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply Input */}
            <div className="reply-container">
              <textarea
                className="reply-input"
                placeholder="Type your reply here..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    handleSendReply();
                  }
                }}
              />
              <button
                className="send-button"
                onClick={handleSendReply}
                disabled={!replyText.trim()}
              >
                Send
              </button>
            </div>
          </>
        ) : (
          <div className="empty-conversation">
            <p>Select a customer to view messages</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerSupport;
